// Ukázky použití: fotky předmětů (láhev, box, sešity…) s místem pro samolepku.
// Do každé fotky se automaticky vloží samolepka, kterou zákazník právě navrhuje.
// Fotky vznikly s prázdnou bílou samolepkou; její rohy, poměr stran a mapa světla
// (stínování) jsou změřené skriptem a samolepka je z fotky vymazaná (public/scenes).
import type { LabelConfig, VariantSettings } from '../types';
import { getSiteTheme } from './settings';
import SCENES_DATA from './usageScenes.data.json';

export type SpotSize = 'large' | 'medium' | 'small';
export interface SceneSpot {
  quad: [number, number][]; // rohy prázdné samolepky v % fotky: vlevo nahoře, vpravo nahoře, vpravo dole, vlevo dole
  aspect: number;           // šířka / výška prázdné samolepky
  shade: string;            // mapa světla (šedotónová PNG, narovnaná na obdélník)
  size: SpotSize;           // jakou samolepku sem dát: největší / střední / nejmenší
  scale?: number;           // zvětšení oproti prázdné samolepce na fotce (bačkory mají místo víc)
  blur?: number;            // ostrost fotky v místě samolepky (px rozostření), výchozí 0.6
  maxGrow?: number;         // kolikrát smí kulatá/úzká samolepka přerůst prázdné místo, aby nebyla moc malá
  shadePad?: string;        // mapa světla rozšířená o okraj WARP_MARGIN (pro ohnuté samolepky přesahující místo)
  relief?: string;          // textura povrchu (šedá, 128 = rovno) na obdélník místa + okraj WARP_MARGIN – kreslí se hard-light
  warp?: string;            // mapa posunu (R vodorovně, G svisle) – ohyb po válci/kopuli a zvlnění podle nerovností
  light?: string;           // barevná mapa světla z okolí na fotce (+ okraj) – nahrazuje shadePad, multiply
  gloss?: string;           // mapa odlesků lesklého povrchu (+ okraj) – screen
  glossOpacity?: number;    // síla odlesků 0–1
  edgeShadow?: number;      // stín pod hranou samolepky (krytí 0–1), světlo shora
  warpAmp?: number;         // rozsah mapy posunu v násobcích výšky místa (feDisplacementMap scale = warpAmp × výška)
}
/** Okraj mapy posunu kolem místa (podíl šířky / výšky) – stejný jako v tools/usage-scenes/relief.py. */
export const WARP_MARGIN = { x: 0.25, y: 0.5 };
export interface UsageScene {
  id: string;
  title: string;
  hint: string;
  img: string;
  w: number;
  h: number;
  theme: string | null; // regulární výraz: fotka jen pro téma (kočičky, pejsci…); null = pro všechny
  spots: SceneSpot[];
}

export const USAGE_SCENES: UsageScene[] = SCENES_DATA as UsageScene[];

/** Fotky pro aktuální téma: nejdřív tematické, pak obecné. */
export function scenesForSite(): UsageScene[] {
  const t = getSiteTheme();
  const key = `${t?.slug || ''} ${t?.name || ''} ${typeof location !== 'undefined' ? location.hostname : ''}`.toLowerCase();
  const themed = USAGE_SCENES.filter(s => s.theme && new RegExp(s.theme, 'i').test(key));
  return themed.concat(USAGE_SCENES.filter(s => !s.theme));
}

export interface StickerPick { variant: string; w: number; h: number }

/** Samolepky z archu, které má smysl ukázat (viditelné, s počtem kusů), od největší. */
export function visibleStickers(config: LabelConfig): StickerPick[] {
  return Object.entries(config.variantSettings || {})
    .filter(([, s]: [string, VariantSettings]) => s && s.visible !== false && (s.customCount ?? 0) > 0)
    .map(([variant, s]) => ({ variant, w: s.customWidth || 26, h: s.customHeight || 20 }))
    .sort((a, b) => b.w * b.h - a.w * a.h);
}

/** Pro každé místo na fotce vybere samolepku: podle velikosti a podobného tvaru, různé tam, kde jde. */
export function pickForSpots(spots: SceneSpot[], stickers: StickerPick[]): (StickerPick | null)[] {
  if (!stickers.length) return spots.map(() => null);
  const used = new Map<string, number>();
  return spots.map(sp => {
    const n = stickers.length;
    let best: StickerPick | null = null, bestScore = Infinity;
    stickers.forEach((s, i) => {
      const rank = n > 1 ? i / (n - 1) : 0; // 0 = největší
      const want = sp.size === 'large' ? 0 : sp.size === 'small' ? 1 : 0.5;
      const shape = Math.abs(Math.log((s.w / s.h) / sp.aspect));
      const score = shape * 0.9 + Math.abs(rank - want) * 1.2 + (used.get(s.variant) || 0) * 0.35;
      if (score < bestScore) { bestScore = score; best = s; }
    });
    if (best) used.set(best!.variant, (used.get(best!.variant) || 0) + 1);
    return best;
  });
}

type P = [number, number];

/** Projektivní transformace jednotkového čtverce na čtyřúhelník (Heckbert). */
function unitToQuad(q: P[]) {
  const [[x0, y0], [x1, y1], [x2, y2], [x3, y3]] = q;
  const dx1 = x1 - x2, dx2 = x3 - x2, dx3 = x0 - x1 + x2 - x3;
  const dy1 = y1 - y2, dy2 = y3 - y2, dy3 = y0 - y1 + y2 - y3;
  let g = 0, h = 0;
  if (Math.abs(dx3) > 1e-9 || Math.abs(dy3) > 1e-9) {
    const den = dx1 * dy2 - dx2 * dy1;
    g = (dx3 * dy2 - dx2 * dy3) / den;
    h = (dx1 * dy3 - dx3 * dy1) / den;
  }
  return { a: x1 - x0 + g * x1, b: x3 - x0 + h * x3, c: x0, d: y1 - y0 + g * y1, e: y3 - y0 + h * y3, f: y0, g, h };
}
const apply = (m: ReturnType<typeof unitToQuad>, u: number, v: number): P => {
  const z = m.g * u + m.h * v + 1;
  return [(m.a * u + m.b * v + m.c) / z, (m.d * u + m.e * v + m.f) / z];
};

export interface SpotLayout {
  matrix: string;      // CSS matrix3d pro prvek o rozměru boxW × boxH px
  boxW: number; boxH: number;
  pxPerMm: number;     // kolik px fotky (1200 px šířka) připadá na 1 mm samolepky – pro rozostření a zrno
  uw: number; vh: number; // jakou část prázdné samolepky (šířka, výška) zabírá vložená samolepka
}

/**
 * Kam a jak natočit samolepku o poměru stran w:h, aby se vešla do místa na fotce (sceneW × sceneH px).
 * Samolepka se vejde celá ("contain"), vystředěná.
 */
export function layoutSpot(spot: SceneSpot, w: number, h: number, sceneW: number, sceneH: number): SpotLayout {
  const quad = spot.quad.map(([x, y]) => [x / 100 * sceneW, y / 100 * sceneH] as P);
  const ar = w / h;
  let uw = 1, vh = 1;
  if (ar >= spot.aspect) vh = spot.aspect / ar; else uw = ar / spot.aspect;
  // kulatá nebo úzká samolepka by v podlouhlém místě vyšla malá – zvětšit ji k ~85 % plochy místa
  const grow = Math.min(spot.maxGrow ?? 1.4, Math.max(1, Math.sqrt(0.85 / (uw * vh))));
  const k = grow * (spot.scale ?? 1);
  uw *= k; vh *= k;
  const u0 = (1 - uw) / 2, v0 = (1 - vh) / 2;
  const M = unitToQuad(quad);
  const sub = [apply(M, u0, v0), apply(M, u0 + uw, v0), apply(M, u0 + uw, v0 + vh), apply(M, u0, v0 + vh)];
  const S = unitToQuad(sub);
  const boxW = Math.round(w * 10), boxH = Math.round(h * 10);
  // jednotkový čtverec → px prvku: dělit šířkou a výškou prvku
  const a = S.a / boxW, d = S.d / boxW, g = S.g / boxW;
  const b = S.b / boxH, e = S.e / boxH, hh = S.h / boxH;
  const matrix = `matrix3d(${[a, d, 0, g, b, e, 0, hh, 0, 0, 1, 0, S.c, S.f, 0, 1].map(n => +n.toFixed(8)).join(',')})`;
  return {
    matrix, boxW, boxH, uw, vh,
    pxPerMm: Math.hypot(sub[1][0] - sub[0][0], sub[1][1] - sub[0][1]) / w,
  };
}
